import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, ChevronRight, PackageX, Star, FileDown } from 'lucide-react';
import { useApp } from '../context/AppContext';
import BottomNav from '../components/BottomNav';
import DesktopHeader from '../components/DesktopHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INVOICE_STATES = new Set(['Completed', 'completed', 'Paid', 'paid', 'Finished', 'finished']);

const downloadInvoice = async (bookingId) => {
  const token = localStorage.getItem('c24_token');
  try {
    const r = await fetch(`${API}/bookings/${bookingId}/invoice.pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) {
      alert(r.status === 403 ? 'Not authorised to download this invoice' : `Could not fetch invoice (HTTP ${r.status})`);
      return;
    }
    const invoiceNo = r.headers.get('x-invoice-number') || bookingId;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${invoiceNo}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    alert('Download failed: ' + (e?.message || e));
  }
};

const Bookings = () => {
  const nav = useNavigate();
  const { bookings } = useApp();

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      <div className="md:hidden px-5 pt-6 pb-4 bg-white border-b border-neutral-100">
        <h1 className="text-2xl font-extrabold">My Bookings</h1>
        <p className="text-sm text-neutral-500 mt-0.5">Track and manage your service bookings</p>
      </div>

      <div className="max-w-[1100px] mx-auto px-5 md:px-6 pt-4 md:pt-8">
        <div className="hidden md:flex items-end justify-between mb-5">
          <div>
            <h1 className="text-2xl font-extrabold">My Bookings</h1>
            <p className="text-sm text-neutral-500 mt-0.5">Track and manage your service bookings</p>
          </div>
          <div className="flex gap-1 bg-white border border-neutral-200 rounded-lg p-0.5">
            <button className="px-4 py-1.5 text-xs font-semibold bg-[#2E7D32] text-white rounded">All</button>
            <button className="px-4 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 rounded">Upcoming</button>
            <button className="px-4 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 rounded">Completed</button>
          </div>
        </div>

        {bookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
            <div className="w-20 h-20 rounded-full bg-neutral-100 flex items-center justify-center">
              <PackageX className="w-9 h-9 text-neutral-400" />
            </div>
            <p className="mt-4 font-semibold">No bookings yet</p>
            <p className="text-sm text-neutral-500 mt-1">Your bookings will appear here</p>
            <button onClick={() => nav('/home')} className="mt-5 bg-[#2E7D32] text-white px-5 py-2.5 rounded-lg font-semibold">Book a service</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {bookings.map((b) => (
              <div key={b.id} className="bg-white border border-neutral-200 rounded-xl hover:shadow-md hover:border-[#2E7D32] transition-all">
                <button onClick={() => nav(`/booking/${b.id}`)} className="w-full text-left p-4">
                <div className="flex items-start gap-3">
                  <img src={b.items[0]?.image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="inline-flex items-center gap-1 bg-[#E8F5E9] text-[#2E7D32] text-[10px] font-bold px-2 py-0.5 rounded-full">{b.status}</div>
                        <p className="mt-1 font-bold text-sm truncate">{b.items.map((i) => i.name).join(', ')}</p>
                        <p className="text-[11px] text-neutral-500">{b.id}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-neutral-600">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(b.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {b.slot}</span>
                      <span className="ml-auto font-bold text-[#EA580C] text-sm">₹{b.total}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-neutral-500 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" /> {b.address}
                    </div>
                  </div>
                </div>
                </button>
                {INVOICE_STATES.has(b.status) && (
                  <div className="border-t border-neutral-100 px-4 py-2 flex items-center justify-end">
                    <button
                      data-testid={`booking-download-invoice-${b.id}`}
                      onClick={(e) => { e.stopPropagation(); downloadInvoice(b.id); }}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2E7D32] hover:text-[#1B5E20]">
                      <FileDown className="w-3.5 h-3.5" />
                      Download Tax Invoice
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Bookings;
